/**
 * 근태 쿼리 키 팩토리.
 *
 * 근태 mutation 은 **같은 파일 아래의 `defineInvalidation` 계약**을 거쳐 무효화한다 — 각 훅이
 * 어떤 scope(`me` · `calendar` · `weeklySummary` · `admin`)를 갱신하는지 선언한다.
 * 예전에는 8곳이 전부 `predicate: (q) => q.queryKey[0] === attendanceKeys.all[0]` 로 루트
 * 전체를 지웠다(X3 단계 1d/3 D6·D7 에서 admin scope 와 PLAYER scope 를 한 루트로 통합한 결과).
 *
 * **조회 키를 이 루트 안에 두는 것은 여전히 전제다** — scope 접두사가 전부 루트 아래에 있으므로,
 * 루트 밖으로 나가면 어떤 선언에도 안 걸려 타입 에러 없이 조용히 stale 된다.
 * 실제로 월간 캘린더·주간 합계가 **단수** `['attendance', …]` 로 나가 있었고, 퇴근을 눌러도
 * 히트맵의 오늘 칸이 "근무 중" 인 채로, 주간 합계도 옛 값으로 남았다(로컬 실측).
 * 회귀 가드: `__tests__/attendanceKeys.test.ts` · `__tests__/attendanceRootCoverage.dom.test.tsx`.
 *
 * **여기에 키를 추가할 때는 반드시 `all` 을 prefix 로 둘 것** — 그러면 기존 mutation 은
 * 물론 앞으로 추가될 mutation 도 별도 무효화 없이 자동 커버된다. `defineQueryKeys` 가 그걸
 * 타입으로 강제한다: 루트를 첫 인자로 한 번만 받고, 멤버가 만드는 키가 `['attendances', …]`
 * 로 시작하지 않으면 컴파일이 거부한다. 위 단수/복수 사고가 다시 나려면 이제 타입을 뚫어야 한다.
 *
 * 루트 밖인 이웃 네임스페이스는 **의도적**이라 여기 넣지 않는다:
 * `['attendance-audit', …]` 은 근태 mutation 이 건드리지 않도록 테스트가 못박고 있고
 * (`attendanceRecordHandlers.test.ts` · `useApproveCorrection.test.tsx` 의 "다른 prefix 안 건드림"),
 * `['corrections', …]` 은 정정 도메인 자체의 루트다. 이 둘은 각자의 팩토리를 가질 대상이지
 * 여기에 끼워 넣을 것이 아니다.
 */
import { defineInvalidation, defineQueryKeys } from '@/shared/lib/queryKeys';

const keys = defineQueryKeys('attendances', (all) => ({
  /**
   * ── 무효화 전용 중간 접두사 ────────────────────────────────────────────
   *
   * 조회에 쓰지 않는다. 뮤테이션이 **무엇을 무효화하는지 선언**할 때 쓴다
   * (아래 `defineInvalidation` 계약).
   *
   * 이름이 `*All` 이 아니라 `*Prefix` 인 것은 **`adminAll` 이 이미 조회 키**이기 때문이다
   * (`['attendances','admin','all',start,end]`). 다른 팩토리(`checkpointKeys` 등)는 `*All` 을
   * 쓰지만 여기서는 충돌을 피해 네 개 모두 `*Prefix` 로 맞췄다.
   */
  mePrefix: [...all, 'me'] as const,
  calendarPrefix: [...all, 'calendar'] as const,
  weeklySummaryPrefix: [...all, 'weekly-summary'] as const,
  periodSummaryPrefix: [...all, 'period-summary'] as const,
  /** admin 조회 4종(`adminAll` · `adminTeam` · `adminAggregate` · `adminUser`)을 한 번에 덮는다. */
  adminPrefix: [...all, 'admin'] as const,

  me: (startDate: string | null, endDate: string | null) =>
    [...all, 'me', startDate, endDate] as const,
  /**
   * 기간 없이 내 근태 전체 — `features/check-in` 의 조회와 `widgets/CarouselDock` 의 hover 워밍이
   * **같은 키를 공유해야 한다.** `me(null, null)` 과는 칸 수가 달라(2 vs 4) 별도 캐시 엔트리다.
   * 리터럴이던 시절 두 곳이 각자 `['attendances', 'me']` 를 적고 있었다 — 한쪽만 바뀌면 워밍이
   * 딴 키를 채워 캐시 미스가 조용히 생긴다.
   */
  mine: () => [...all, 'me'] as const,
  calendar: (month: string | undefined) => [...all, 'calendar', month] as const,
  weeklySummary: (weekStart: string | undefined) => [...all, 'weekly-summary', weekStart] as const,
  /** 고른 기간 합계 — 주간과 달리 시작·종료 두 칸이 캐시를 가른다. */
  periodSummary: (startDate: string | undefined, endDate: string | undefined) =>
    [...all, 'period-summary', startDate, endDate] as const,
  adminAll: (startDate: string | undefined, endDate: string | undefined) =>
    [...all, 'admin', 'all', startDate, endDate] as const,
  adminTeam: (teamId: string | undefined) => [...all, 'admin', 'team', teamId] as const,
  adminAggregate: (startDate: string | undefined, endDate: string | undefined) =>
    [...all, 'admin', 'aggregate', startDate, endDate] as const,
  adminUser: (userId: string | undefined) => [...all, 'admin', 'user', userId] as const,
}));

/**
 * 무효화 계약.
 *
 * - `me` — 내 근태(`useMyAttendances` · `useCheckIn`). 기간 지정/미지정 둘 다 덮는다
 * - `calendar` — 월간 히트맵. **정정 배지(`correction?: { status }`)를 싣는 유일한 화면**이다
 *   (`shared/types/attendance.ts` 의 `CalendarHeatmapCellProps` · `CalendarDayData`)
 * - `weeklySummary` — 주간 합계. **실제 시각을 합산**하므로 시각이 바뀔 때만 영향받는다
 * - `periodSummary` — 고른 기간 합계. 주간과 같은 소스를 더하므로 같은 자리에서 함께 선언한다
 * - `admin` — 관리자 근태 4종(전사·팀·집계·개인)
 *
 * 예전에는 8곳이 `predicate: (q) => q.queryKey[0] === attendanceKeys.all[0]` 로 루트 전체를
 * 지웠다 — 문자열 비교라 타입 보호가 닿지 않던 유일한 자리였다.
 *
 * `attendanceRoot` 는 **배럴로 내보내지 않는다** — 열면 계약을 우회할 수 있다.
 */
export const {
  keys: attendanceKeys,
  root: attendanceRoot,
  invalidate: invalidateAttendanceScopes,
} = defineInvalidation(keys, {
  me: keys.mePrefix,
  calendar: keys.calendarPrefix,
  weeklySummary: keys.weeklySummaryPrefix,
  periodSummary: keys.periodSummaryPrefix,
  admin: keys.adminPrefix,
});
